import React, { FC, useMemo } from 'react';
import { Tree } from '@/layout/Tree/Tree';
import { JsonTreeNode } from './JsonTreeNode';
import { parseJsonTree } from './utils';

export interface JsonTreeProps {
  /**
   * The data to be rendered as a JSON tree.
   */
  data: { [key: string]: any };

  /**
   * If true, all nodes in the JSON tree will be expanded by default.
   * @default false
   */
  showAll?: boolean;

  /**
   * The limit for the number of nodes to show when `showAll` is false.
   * @default 10
   */
  showAllLimit?: number;

  /**
   * If true, the count of child nodes will be shown next to each node.
   * @default true
   */
  showCount?: boolean;

  /**
   * If true, empty nodes will be shown in the JSON tree.
   * @default true
   */
  showEmpty?: boolean;

  /**
   * If true, long text in nodes will be truncated and replaced with an ellipsis.
   * @default true
   */
  ellipsisText?: boolean;

  /**
   * The maximum length of text in a node before it is truncated and replaced with an ellipsis.
   * @default 150
   */
  ellipsisTextLength?: number;

  /**
   * The depth at which the JSON tree nodes should be expanded by default.
   * @default 2
   */
  expandDepth?: number;

  /**
   * If true, the JSON tree will be rendered with a root node.
   * @default true
   */
  root?: boolean;

  /**
   * The CSS class name to be applied to the JSON tree.
   */
  className?: string;
}

export const JsonTree: FC<JsonTreeProps> = ({
  data,
  className,
  expandDepth = 2,
  showEmpty = true,
  showCount = true,
  showAll = false,
  showAllLimit = 10,
  ellipsisText = true,
  ellipsisTextLength = 150,
  root = true,
  ...rest
}) => {
  const tree = parseJsonTree({ data, showEmpty });

  return (
    <div tabIndex={-1}>
      <Tree className={className} {...rest}>
        {root && (
          <JsonTreeNode
            key={`node-${tree.id}`}
            depth={1}
            data={tree}
            showEmpty={showEmpty}
            showCount={showCount}
            expandDepth={expandDepth}
            ellipsisText={ellipsisText}
            ellipsisTextLength={ellipsisTextLength}
            showAll={showAll}
            showAllLimit={showAllLimit}
          />
        )}
        {!root &&
          tree?.data?.map(item => (
            <JsonTreeNode
              key={`node-${item.id}`}
              depth={1}
              data={item}
              showEmpty={showEmpty}
              showCount={showCount}
              expandDepth={expandDepth}
              ellipsisText={ellipsisText}
              ellipsisTextLength={ellipsisTextLength}
              showAll={showAll}
              showAllLimit={showAllLimit}
            />
          ))}
      </Tree>
    </div>
  );
};
